/**
 * 話者分離の API の応答 (`POST /api/diarization/execute`・`GET /api/diarization/{meeting_id}`、PBI-004 §2.3)
 * を、画面の内部の形に変える。
 *
 * **API の形に依る処理はこのファイルだけに置く**（API の形が変わっても描画側を直さないため。#1247 段階分けの案）。
 * 描画側の部品（DiarizationResult 等）は、ここが返す形を前提にする。
 */

/** 画面の処理状態 (CD-007・SCR-D-001 DP-D-004)。`loading` は画面だけの状態で、ここでは返さない */
export const DIARIZATION_PHASE = {
    idle: 'idle',
    loading: 'loading',
    pending: 'pending',
    done: 'done',
    error: 'error',
};

/** API の `status` → 画面の状態 (SCR-D-001 DP-D-004 の対応表) */
const STATUS_TO_PHASE = {
    not_started: DIARIZATION_PHASE.idle,
    pending: DIARIZATION_PHASE.pending,
    done: DIARIZATION_PHASE.done,
    failed: DIARIZATION_PHASE.error,
};

const RATIO_KEYS = ['agent', 'candidate', 'unknown'];

function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 比率を画面の形にする。数値でないキー・欠けたキーは 0 にする
 * （そのまま渡すと画面に `NaN%` が出る。段階 1 の Step 2 レビュー [SHOULD] 1）。
 *
 * @param {unknown} speakerRatio API の `result.speaker_ratio`
 * @returns {{agent: number, candidate: number, unknown: number}}
 */
export function toRatio(speakerRatio) {
    const source = speakerRatio && typeof speakerRatio === 'object' ? speakerRatio : {};
    return RATIO_KEYS.reduce((ratio, key) => {
        ratio[key] = isFiniteNumber(source[key]) ? source[key] : 0;
        return ratio;
    }, {});
}

/**
 * 発話の列を画面の形にする。`index` が整数でないときは並びの位置を使う（key と id に使うため）。
 * `uncertain` は画面の表示に使わない（DP-D-005 は `role` で出す。SCR-D-001 1.6）ため持たない。
 *
 * @param {unknown} utterances API の `result.utterances`
 * @returns {Array<{index: number, role: string, text: string}>}
 */
export function toUtterances(utterances) {
    if (!Array.isArray(utterances)) return [];
    return utterances
        .filter((u) => u && typeof u === 'object')
        .map((u, position) => ({
            index: Number.isInteger(u.index) ? u.index : position,
            role: typeof u.role === 'string' ? u.role : '',
            text: typeof u.text === 'string' ? u.text : '',
        }));
}

/**
 * @param {unknown} topics API の `result.topics`
 * @returns {string[]}
 */
export function toTopics(topics) {
    if (!Array.isArray(topics)) return [];
    return topics.filter((t) => typeof t === 'string');
}

/**
 * @param {unknown} result API の `result`（`done` のときだけ値がある）
 * @returns {{utterances: Array, ratio: Object, topics: string[], roleConfidence: string} | null}
 */
export function toResult(result) {
    if (!result || typeof result !== 'object') return null;
    return {
        utterances: toUtterances(result.utterances),
        ratio: toRatio(result.speaker_ratio),
        topics: toTopics(result.topics),
        // `low` のときだけ DP-D-009 を出す。それ以外（欠け・想定外の値）はバナーを出さない側に倒す
        roleConfidence: result.role_confidence === 'low' ? 'low' : 'high',
    };
}

/**
 * API の応答全体を、画面の状態にする。
 *
 * 仮定: 想定外の `status` は `error`（再実行できない失敗）として扱う（「未実行」と見せて実行させると、
 * 処理中・完了済みの面談に二重に実行を受け付けさせるおそれがあるため。SCR-D-001 に定めが無い。未確認）
 *
 * @param {unknown} body API の応答本文
 * @returns {{
 *   phase: string,
 *   acceptedAt: string | null,
 *   failure: {code: (string|null), retryable: boolean, message: string, retryFrom: (string|null),
 *             unknownStatus: boolean} | null,
 *   result: Object | null,
 * }}
 */
export function toDiarizationState(body) {
    const source = body && typeof body === 'object' ? body : {};
    const knownPhase = STATUS_TO_PHASE[source.status];
    const phase = knownPhase || DIARIZATION_PHASE.error;
    return {
        phase,
        acceptedAt: typeof source.accepted_at === 'string' ? source.accepted_at : null,
        failure: knownPhase ? toFailure(source.failure) : UNKNOWN_STATUS_FAILURE,
        result: phase === DIARIZATION_PHASE.done ? toResult(source.result) : null,
    };
}

/**
 * 再実行をどこから行うか (`failure.retry_from`。PBI-004 §6。#1246 段階 4)。
 * 役割の判定からやり直す経路は、音声も長さの上限も見ず、AI ポイントを消費しない（SCR-D-001 §4.3 BTN-D-003）
 */
export const RETRY_FROM = {
    roleJudgement: 'role_judgement',
    speechRecognition: 'speech_recognition',
};

const RETRY_FROM_VALUES = Object.values(RETRY_FROM);

/**
 * 想定外の `status` のときの失敗（再実行できない側に倒す）。
 * `unknownStatus` は失敗の区分ではなく画面とサーバの食い違いの印で、画面はこれを見て文言を選ぶ
 * （文言はここに持たない。描画側の文言を API の変換に混ぜないため）
 */
const UNKNOWN_STATUS_FAILURE = { code: null, retryable: false, message: '', retryFrom: null, unknownStatus: true };

/**
 * @param {unknown} failure API の `failure`（`failed` のときだけ値がある）
 * @returns {{code: (string|null), retryable: boolean, message: string, retryFrom: (string|null),
 *            unknownStatus: boolean} | null}
 *   retryFrom は RETRY_FROM の値。欠け・想定外の値は null（役割の判定からの経路として扱わない。
 *   扱うと、音声の応答を見ずに、サーバが断る再実行のボタンを出しうるため）
 */
export function toFailure(failure) {
    if (!failure || typeof failure !== 'object') return null;
    return {
        code: typeof failure.code === 'string' ? failure.code : null,
        retryable: failure.retryable === true,
        message: typeof failure.message === 'string' ? failure.message : '',
        retryFrom: RETRY_FROM_VALUES.includes(failure.retry_from) ? failure.retry_from : null,
        unknownStatus: false,
    };
}
